import React from "react";
import styled, { css, useTheme } from "styled-components";
import PropTypes from "prop-types";
import Button from "components/Button";
import Image from "next/image";
import Link from "next/link";

const OuterWrapper = styled.section`
  background-color: ${({ theme, backgroundColor }) =>
    backgroundColor || theme.themeColors.primary};
  color: ${({ theme, fontColor }) => fontColor || theme.themeColors.white};
  display: flex;
  flex-direction: column;
  padding: 4rem 1.5rem 0;
  width: 100%;

  ${({ theme }) => theme.mq.md`
    padding: 5rem 1.5rem 0;
  `}

  ${({ theme }) => theme.mq.lg`
    flex-direction: row;
    justify-content: space-between;
    padding: 0;
  `}
`;

const ImageOuterWrapper = styled.div`
  padding: 0 1.5rem;

  ${({ theme }) => theme.mq.lg`
    display: flex;
    background-color: ${theme.themeColors.mediumMint};
    justify-content: flex-end;
    min-height: 42rem;
    order: ${({ imageOrder }) => imageOrder};
    padding: 0 5rem;
    width: 50%;

    ${({ imageOrder }) =>
      imageOrder === 2 &&
      css`
        justify-content: flex-start;
      `}
  `}

  ${({ theme }) => theme.mq.xl`
    min-height: 45rem;
  `}
`;

const ImageWrapper = styled.div`
  height: 100%;
  margin: 0 auto 1.5rem;
  position: relative;
  max-width: 42.5rem;
  min-height: 15.5rem;
  width: 100%;

  ${({ theme }) => theme.mq.md`
    display: flex;
    flex-direction: column;
  `}

  ${({ theme }) => theme.mq.lg`
    margin: 0 0 1.5rem;
    max-width: 40rem;
  `}
`;

const TextButtonOuterWrapper = styled.div`
  display: flex;
  padding: 3.5rem 1.5rem;

  ${({ theme }) => theme.mq.lg`
    align-self: center;
    justify-content: flex-start;
    order: 2;
    padding: 2rem 4rem;
    width: 50%;

    ${({ imageOrder }) =>
      imageOrder === 2 &&
      css`
        justify-content: flex-end;
        order: 1;
      `}
  `}

  ${({ theme }) => theme.mq.xl`
    padding: 2rem 6rem;
  `}
`;

const TextButtonWrapper = styled.div`
  ${({ theme }) => theme.mq.xl`
    max-width: 35rem;
  `}
`;

const Title = styled.h3`
  font-size: 2.25rem;
  letter-spacing: -0.066875rem;
  margin: 0 auto 1.5rem;
  width: 100%;

  span {
    font-family: ${({ theme }) => theme.fontStacks.serifItalic};
    font-style: italic;
  }

  ${({ theme }) => theme.mq.md`
    font-size: 2.875rem;

    br {
      display: none;
    }
  `}

  ${({ theme }) => theme.mq.lg`
    font-size: 3.5rem;

    br {
      display: block;
    }
  `}
`;

const Description = styled.p`
  font-size: 1.25rem;
  margin: 0 auto 3rem;
`;

const ButtonWrapper = styled.a`
  display: flex;
  max-width: 12.75rem;
  width: 100%;

  button {
    height: 3.375rem;
  }
`;

function ImageTextCta({
  backgroundColor,
  fontColor,
  imageAlt,
  imageOrder,
  imageSrc,
  description,
  title,
  buttonColor,
  buttonLink,
  buttonText,
  buttonTextColor,
}) {
  const theme = useTheme();
  return (
    <OuterWrapper
      backgroundColor={theme.themeColors[backgroundColor]}
      fontColor={theme.themeColors[fontColor]}
    >
      <ImageOuterWrapper imageOrder={imageOrder}>
        <ImageWrapper>
          <Image
            alt={imageAlt}
            priority
            layout="fill"
            objectFit="contain"
            src={imageSrc}
          />
        </ImageWrapper>
      </ImageOuterWrapper>
      <TextButtonOuterWrapper imageOrder={imageOrder}>
        <TextButtonWrapper>
          <Title>{title}</Title>
          <Description>{description}</Description>
          <Link href={buttonLink} passHref>
            <ButtonWrapper>
              <Button
                textColor={buttonTextColor}
                colorType={buttonColor}
                outlineType="solid"
              >
                {buttonText}
              </Button>
            </ButtonWrapper>
          </Link>
        </TextButtonWrapper>
      </TextButtonOuterWrapper>
    </OuterWrapper>
  );
}

export default ImageTextCta;

ImageTextCta.propTypes = {
  backgroundColor: PropTypes.string,
  fontColor: PropTypes.string,
  imageOrder: PropTypes.number,
  imageAlt: PropTypes.string,
  imageSrc: PropTypes.string,
  description: PropTypes.shape({}),
  title: PropTypes.shape({}),
  buttonTextColor: PropTypes.string,
  buttonColor: PropTypes.string,
  buttonLink: PropTypes.string,
  buttonText: PropTypes.string,
};

ImageTextCta.defaultProps = {
  backgroundColor: "",
  description: {},
  fontColor: "",
  imageAlt: "",
  imageOrder: undefined,
  imageSrc: "",
  title: {},
  buttonColor: "",
  buttonText: "Join GoodFriend",
  buttonLink: "/search",
  buttonTextColor: "",
};
